import React, { useCallback, useEffect, useState } from "react";
import {
    Box,
    Typography,
    Stack,
    Divider,
    IconButton,
    Collapse,
    styled,
} from "@mui/material";
import CustomIcon from "../../../../Components/CustomIcon";
import CartLoader from "../../../../Helper/Loaders/CartLoader";
import { useTranslation } from "react-i18next";
import { fetchDigitalTwinHierarchy, getHierarchyEntityTypes, shouldDisabledNode } from "../../../../Helper/DigitalTwin/DigitalTwinFunctions";
import { useBatteryConfig, useBatteryInfo } from "../../../../Pages/DigitalTwinV3/DigitalTwinContext";
import BatterySidebarContainer from "../../components/BatterySidebarContainer";
import { usePagesContent } from "../../../../Hooks/usePagesContent";

const NodeItemParent = styled(Box)(({ isLeaf, hasOnlyOnePack, level }) => ({
    marginBottom: isLeaf ? 0 : '4px',
    '.root-node': {
        display: "flex",
        alignItems: "center",
        width: '100%',
        position: 'relative',
    },
    ...(!hasOnlyOnePack && {
        '.root-node > :first-child': {
            borderLeft: "0.5px solid #BBBBBB",
        }
    }),
    ...(isLeaf && !hasOnlyOnePack && {
        '&:last-child .root-node > :first-child': {
            borderLeft: '0.5px solid transparent',
            position: 'relative',
            '::before': {
                content: '""',
                position: "absolute",
                top: 0,
                height: "50%",
                left: "-0.56px",
                width: 0,
                borderLeft: "0.5px solid #BBBBBB",
            }
        }
    })
}));

const NodeItem = styled(Stack)(({ selected, highlight, isLeaf, hasOnlyOnePack }) => ({
    backgroundColor: selected && !isLeaf ? "#EFF0FF" : "inherit",
    borderRadius: 4,
    cursor: "pointer",
    paddingTop: isLeaf ? 0 : '2px',
    paddingBottom: isLeaf ? 0 : '2px',

    "&.disabled": {
        color: "#A2A2A2 !important",
        cursor: "not-allowed !important",
    },

    "& .icon-button": {
        width: 24,
        height: 24,
        background: "none",
    },
    "&:hover": {
        backgroundColor: selected && !isLeaf ? "#EFF0FF" : (!selected && !isLeaf ? "#F9F9F9" : "inherit"),
    },
    "& .MuiTypography-root": {
        color: (selected || highlight) && (!isLeaf || hasOnlyOnePack) ? "#6D72F6 !important" : "inherit",
        fontWeight: selected ? 500 : (highlight && !isLeaf ? 500 : "inherit"),

        "&:hover": {
            color: (selected || highlight) && !isLeaf ? "#4D44D8 !important" : "inherit",
        },
    },
    "& .last-node-item": {
        borderRadius: "4px",
        backgroundColor: selected ? "#EFF0FF" : "inherit",
        "&:hover": {
            backgroundColor: selected ? "#EFF0FF" : "#F9F9F9",
        }
    },
}));


const getAllChildDisplayIds = (node) => {
    let displayIds = [];
    if (node.children && node.children.length > 0) {
        node.children.forEach((child) => {
            displayIds.push(child.displayId);
            displayIds = displayIds.concat(getAllChildDisplayIds(child));
        });
    }
    return displayIds;
};

const shouldHighlightChildSelected = (node, selectedNodeId, openNodes) => {
    if (!node.children) return false;

    const allChildDisplayIds = getAllChildDisplayIds(node);
    const isChildSelected = allChildDisplayIds.includes(selectedNodeId);
    if (!isChildSelected) return false;

    const isOpen = openNodes[node.displayId];
    const isParentOfLeaf = node.children.every(
        (child) => !child.children || child.children.length === 0
    );

    return isParentOfLeaf || !isOpen;
};

const TwinHierarchyCard = ({ title, currentNode = {}, onChangeNode, tab = 'batteryVitals', digitalTwinHierarchy }) => {
    const batteryConfig = useBatteryConfig();
    const [openNodes, setOpenNodes] = useState({});
    const [selectedNode, setSelectedNode] = useState(currentNode);

    useEffect(() => {
        if (currentNode) {
            setSelectedNode(currentNode);
        } else {
            setSelectedNode(Object.values(digitalTwinHierarchy)?.[0]?.[0]);
        }
    }, [digitalTwinHierarchy, currentNode]);

    useEffect(() => {
        if (selectedNode) {
            onChangeNode(selectedNode);
        }
    }, [selectedNode]);

    const handleToggle = (event, displayId) => {
        event.stopPropagation();
        setOpenNodes((prev) => ({
            ...prev,
            [displayId]: !prev[displayId],
        }));
    };

    const selectNode = (node) => {
        setSelectedNode(node);
        onChangeNode(node);
    };

    if (!digitalTwinHierarchy) {
        return <CartLoader />
    };

    const renderNode = (node, level = 0) => {
        const hasChildren = node.children && node.children.length > 0;
        const isOpen = openNodes[node.displayId] || false;
        const isLeaf = !hasChildren;
        const highlight = shouldHighlightChildSelected(node, selectedNode?.displayId, openNodes);
        const isDisabled = shouldDisabledNode(node.level, batteryConfig)
        const hasOnlyOnePack = isLeaf && level === 0;

        return (
            <NodeItemParent key={node.displayId} className="node-item" isLeaf={isLeaf} hasOnlyOnePack={hasOnlyOnePack} level={level}>
                <NodeItem
                    className={isDisabled ? "disabled" : ""}
                    direction="row"
                    alignItems="center"
                    selected={node.displayId === selectedNode?.displayId}
                    highlight={highlight}
                    isLeaf={isLeaf}
                    hasOnlyOnePack={hasOnlyOnePack}
                    onClick={() => isDisabled ? {} : selectNode(node)}
                >
                    {hasChildren ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', pl: level * 2 }}>
                            <IconButton
                                className="icon-button"
                                onClick={(event) => handleToggle(event, node.displayId)}
                                sx={{ marginLeft: '3px', padding: '12px' }}
                            >
                                <CustomIcon
                                    name="primary-arrow-icon"
                                    rotate={isOpen ? 90 : 0}
                                />
                            </IconButton>
                            <Typography variant="fw400s12c494949" sx={{ lineHeight: 2 }}>{node.label}</Typography>
                        </Box>
                    ) : (
                        <Box className="root-node" sx={{ pl: level * 2 }}>
                            {!hasOnlyOnePack && <Box
                                className="center-update"
                                sx={{ position: "relative", bottom: "4px", width: '22px' }}
                            >
                                <Box sx={{ ml: "-0.76px" }}>
                                    <CustomIcon
                                        color="#BBBBBB"
                                        height="50px"
                                        name="last-node-level-icon"
                                    />
                                </Box>
                            </Box>
                            }
                            <Box sx={{ paddingTop: '7px', paddingBottom: '7px', paddingLeft: '12px', paddingRight: '12px', width: '100%', display: 'flex', my: 0.25 }} className="last-node-item">
                                <Typography variant="fw400s12c494949">{node.label}</Typography>
                            </Box>
                        </Box>
                    )}
                </NodeItem>

                {hasChildren && (
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <Box className="node-children-container">
                            {node.children.map((child) => renderNode(child, level + 1))}
                        </Box>
                    </Collapse>
                )}
            </NodeItemParent>
        );
    };

    return (
        <BatterySidebarContainer title={title} tab={tab}>
            <Box sx={{ overflow: "auto", height: "425px" }}>
                {!selectedNode ? (
                    <CartLoader />
                ) : (
                    <>
                        {
                            Object.values(digitalTwinHierarchy).map((item) => {
                                return item.map((node) => renderNode(node));
                            })
                        }
                    </>
                )}
            </Box>
        </BatterySidebarContainer>
    );
};

export default TwinHierarchyCard;
